import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  ean: string | null
  active: boolean
}

interface Render3D {
  id: number
  productId: number
  variant: string
  cloudLink: string
  notes: string | null
  product: { id: number; sku: string; name: string; brand: string; ean: string | null }
}

export function Randari3DPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.renders:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Render3D | null>(null)

  const rendersQuery = useQuery<{ renders: Render3D[] }>({
    queryKey: ['renders-3d'],
    queryFn: async () => (await api.get<{ renders: Render3D[] }>('/marketing/renders-3d')).data,
  })

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Render3D>) =>
      (await api.post('/marketing/renders-3d', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renders-3d'] })
      toast.success('Render adăugat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Render3D> }) =>
      (await api.patch(`/marketing/renders-3d/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renders-3d'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/renders-3d/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renders-3d'] })
      toast.success('Șters')
    },
  })

  const renders = useMemo(() => rendersQuery.data?.renders ?? [], [rendersQuery.data])
  const pag = useClientPagination(renders, 10)
  const products = useMemo(
    () => (productsQuery.data?.products ?? []).filter((p) => p.active),
    [productsQuery.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Randări 3D</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Render nou</Button>}
      </div>

      <Card description="Bibliotecă de link-uri către randări 3D (Drive / Dropbox / OneDrive). Per SKU, EAN și variantă (ex: 30 cps / 60 cps).">
        {rendersQuery.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : renders.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun render. {canWrite && 'Apasă "+ Render nou".'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">SKU</th>
                  <th className="py-2 pr-3 font-medium">Produs</th>
                  <th className="py-2 pr-3 font-medium">EAN</th>
                  <th className="py-2 pr-3 font-medium">Variantă</th>
                  <th className="py-2 pr-3 font-medium">Link</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((r) => (
                  <tr key={r.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{r.product.sku}</td>
                    <td className="py-2 pr-3 max-w-[260px] truncate" title={r.product.name}>
                      {r.product.name}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{r.product.ean ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">{r.variant}</td>
                    <td className="py-2 pr-3">
                      <a
                        href={r.cloudLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-b4l-blue-dark underline hover:no-underline text-xs"
                      >
                        Deschide ↗
                      </a>
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(r)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi render-ul ${r.variant}?`))
                                deleteMutation.mutate(r.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="randări"
          />
        </div>
      </Card>

      {createOpen && (
        <RenderFormModal
          title="Render 3D nou"
          submitLabel="Adaugă"
          products={products}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <RenderFormModal
          title={`Editează render ${editTarget.variant}`}
          submitLabel="Salvează"
          products={products}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

interface FormProps {
  title: string
  submitLabel: string
  products: Product[]
  initial?: Render3D
  onClose: () => void
  onSubmit: (data: Partial<Render3D>) => void
  submitting: boolean
}

function RenderFormModal({
  title,
  submitLabel,
  products,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [productId, setProductId] = useState<string>(
    initial?.productId ? String(initial.productId) : '',
  )
  const [variant, setVariant] = useState(initial?.variant ?? '')
  const [cloudLink, setCloudLink] = useState(initial?.cloudLink ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const submit = () => {
    if (!productId || !variant.trim() || !cloudLink.trim()) {
      toast.error('Completează produs, variantă și link')
      return
    }
    onSubmit({
      productId: Number(productId),
      variant: variant.trim(),
      cloudLink: cloudLink.trim(),
      notes: notes.trim() || null,
    } as Partial<Render3D>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Produs</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!!initial}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm disabled:bg-b4l-bg"
          >
            <option value="">— alege produs —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} — {p.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Variantă"
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
          hint="ex: 60 cps capac alb, 30 cps etichetă nouă, packshot"
        />
        <Field
          label="Link cloud"
          value={cloudLink}
          onChange={(e) => setCloudLink(e.target.value)}
          hint="Link Google Drive / Dropbox / OneDrive (public sau partajat cu echipa)"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note</label>
          <textarea
            value={notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
